// Fester App-Rahmen (M-20, ST-UX-001): Kopf und Navigation stehen, nur der
// Hauptbereich scrollt. Ein haftender Kopf ueber dem Dokument verdeckte
// gescrollte Ziele (WCAG 2.5.8, axe target-size); im eigenen Scrollbereich
// liegt nichts mehr ueber dem Inhalt.
//
// Nur wo es traegt: breiter als 640 px UND mindestens 481 px hoch, in rem,
// damit grosse Schrift wie ein kleines Fenster zaehlt. Darunter scrollt das
// Dokument wie bisher (Adressleiste am Handy, Ziehen zum Neuladen, Reflow bei
// 400 %). `screen`: beim Drucken faellt der Rahmen weg, und die Seite laeuft
// ueber so viele Blaetter, wie sie lang ist.

import { useEffect, type RefObject } from 'react';
import { LOCATION_EVENT } from './router.tsx';

export const RAHMEN_ABFRAGE = 'screen and (min-width: 40.0625rem) and (min-height: 30.0625rem)';
export const RAHMEN = `@media ${RAHMEN_ABFRAGE}`;

/** Tasten, mit denen der Browser die Seite scrollt, solange nichts den Fokus hat. */
const SCROLLTASTEN = new Set(['PageDown', 'PageUp', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End']);
/** Der Scrollstand des Hauptbereichs im Verlaufseintrag (history.state). */
const SCHLUESSEL = 'liaisonScroll';
/** So lange nach dem Zurueckgehen darf der Inhalt nachladen, bis die alte Stelle erreichbar ist. */
const WIEDERHERSTELLEN_MS = 3000;

function rahmenAktiv(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(RAHMEN_ABFRAGE).matches;
}

function gemerkterStand(state: unknown): number {
  const wert = typeof state === 'object' && state !== null ? (state as Record<string, unknown>)[SCHLUESSEL] : undefined;
  return typeof wert === 'number' && Number.isFinite(wert) ? wert : 0;
}

/**
 * Tastatur, Verlauf und Seitenwechsel fuer den eigenen Scrollbereich.
 *
 * - Steht der Fokus auf body (nach dem Laden), scrollen PageDown und Co. im
 *   Rahmen nichts: der Browser scrollt das Dokument, und das steht. Die Taste
 *   setzt den Fokus deshalb vorher in den Hauptbereich und scrollt dort. Beim
 *   Laden selbst bleibt der Fokus, wo er ist: der Skiplink bleibt erster Halt.
 * - Steht der Fokus in Kopf oder Navigation, blaettern Bild auf/ab den
 *   Hauptbereich, ohne den Fokus zu bewegen; die uebrigen Tasten gehoeren dort
 *   den Bedienelementen.
 * - Ein neuer Seitenwechsel beginnt oben; Zurueck, Vor und Neuladen kehren an
 *   die Stelle zurueck, an der man war. Der Stand steht im Verlaufseintrag.
 *   Nachgeladene Seiten ("Weitere laden") laedt die Liste dabei nicht nach; dann
 *   endet die Rueckkehr am Ende dessen, was steht.
 */
export function useRahmenScroll(main: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const bereich = main.current;
    if (bereich === null) return undefined;

    let pfad = window.location.pathname;
    let ausstehend = false;
    let merken: number | undefined;
    let wiederherstellen: number | undefined;

    const jetztMerken = (): void => {
      if (!ausstehend) return;
      ausstehend = false;
      window.clearTimeout(merken);
      const state: unknown = window.history.state;
      const alt = typeof state === 'object' && state !== null ? state : {};
      window.history.replaceState({ ...alt, [SCHLUESSEL]: bereich.scrollTop }, '');
    };
    // Nicht bei jedem Scrollereignis: der Browser drosselt haeufige
    // replaceState-Aufrufe. Vor jeder Handlung, die wegfuehren kann, sofort.
    const beimScrollen = (): void => {
      ausstehend = true;
      window.clearTimeout(merken);
      merken = window.setTimeout(jetztMerken, 100);
    };

    const anhalten = (): void => {
      if (wiederherstellen !== undefined) window.cancelAnimationFrame(wiederherstellen);
      wiederherstellen = undefined;
    };
    // Der Inhalt laedt nach dem Zurueckgehen neu; die alte Stelle ist erst
    // erreichbar, wenn er lang genug ist. Jede eigene Handlung beendet den Versuch.
    const zurueckAn = (ziel: number): void => {
      anhalten();
      bereich.scrollTop = ziel;
      if (ziel <= 0) return;
      const beginn = performance.now();
      const schritt = (): void => {
        bereich.scrollTop = ziel;
        if (Math.abs(bereich.scrollTop - ziel) < 1 || performance.now() - beginn > WIEDERHERSTELLEN_MS) {
          wiederherstellen = undefined;
          return;
        }
        wiederherstellen = window.requestAnimationFrame(schritt);
      };
      wiederherstellen = window.requestAnimationFrame(schritt);
    };

    // Ein Fokuswechsel ausserhalb des Hauptbereichs (Screenreader, Sprachsteuerung)
    // beendet die Rueckkehr wie eine Taste; der Fokus auf main selbst kommt vom Wechsel.
    const beiFokus = (event: FocusEvent): void => {
      if (event.target !== bereich) anhalten();
    };

    const beiVerlauf = (event: PopStateEvent): void => {
      ausstehend = false;
      pfad = window.location.pathname;
      zurueckAn(gemerkterStand(event.state));
    };
    // Eigener Wechsel (router.tsx): auf eine andere Adresse oben beginnen. Ein
    // Parameter derselben Seite (Register, Ansicht) laesst die Stelle stehen.
    const beiWechsel = (): void => {
      if (window.location.pathname === pfad) {
        // Der neue Eintrag (pushState mit null) bekommt die Stelle gleich mit,
        // sonst fuehrte Vor spaeter nach oben.
        ausstehend = true;
        jetztMerken();
        return;
      }
      pfad = window.location.pathname;
      ausstehend = false;
      anhalten();
      bereich.scrollTop = 0;
    };

    const beiTaste = (event: KeyboardEvent): void => {
      jetztMerken();
      anhalten();
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const aktiv = document.activeElement;
      // Ein offenes Popup oder ein Dialog hat seine eigene Tastatur.
      const popup = document.querySelector('[aria-modal="true"], [role="menu"], [role="listbox"], [role="dialog"]') !== null;
      if (popup || !rahmenAktiv()) return;
      if ((aktiv === null || aktiv === document.body) && SCROLLTASTEN.has(event.key)) {
        bereich.focus({ preventScroll: true });
      } else if ((event.key === 'PageDown' || event.key === 'PageUp') && aktiv?.closest('header, nav') != null) {
        event.preventDefault();
        bereich.scrollBy({ top: (event.key === 'PageDown' ? 1 : -1) * bereich.clientHeight * 0.875 });
      }
    };
    const beiZeiger = (): void => {
      jetztMerken();
      anhalten();
    };

    // Neuladen: der Stand des Eintrags gilt wie bei Zurueck.
    if (rahmenAktiv()) zurueckAn(gemerkterStand(window.history.state));

    bereich.addEventListener('scroll', beimScrollen, { passive: true });
    bereich.addEventListener('wheel', anhalten, { passive: true });
    bereich.addEventListener('touchstart', anhalten, { passive: true });
    window.addEventListener('popstate', beiVerlauf);
    window.addEventListener(LOCATION_EVENT, beiWechsel);
    document.addEventListener('keydown', beiTaste, true);
    document.addEventListener('pointerdown', beiZeiger, true);
    document.addEventListener('focusin', beiFokus);
    window.addEventListener('pagehide', jetztMerken);
    return () => {
      anhalten();
      window.clearTimeout(merken);
      bereich.removeEventListener('scroll', beimScrollen);
      bereich.removeEventListener('wheel', anhalten);
      bereich.removeEventListener('touchstart', anhalten);
      window.removeEventListener('popstate', beiVerlauf);
      window.removeEventListener(LOCATION_EVENT, beiWechsel);
      document.removeEventListener('keydown', beiTaste, true);
      document.removeEventListener('pointerdown', beiZeiger, true);
      document.removeEventListener('focusin', beiFokus);
      window.removeEventListener('pagehide', jetztMerken);
    };
  }, [main]);
}
